import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, ShieldCheck, Trash2, Upload, Star } from 'lucide-react';
import api from '@/lib/axios';
import { resolveImageUrl } from '@/lib/listing';
import { useSeo } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';

const FUEL = [['gasolina', 'Gasolina'], ['gasoleo', 'Gasóleo'], ['gpl', 'GPL'],
  ['eletrico', 'Elétrico'], ['hibrido', 'Híbrido'], ['hibrido_plugin', 'Híbrido plug-in']];
const ESTADOS = [['operacional', 'Operacional'], ['operacional_anomalias', 'Operacional com anomalias'],
  ['avariada', 'Avariada'], ['imobilizada', 'Imobilizada'], ['pecas', 'Para peças']];
const VENDEDORES = [['particular', 'Particular'], ['empresa', 'Empresa'],
  ['profissional', 'Profissional'], ['stand', 'Stand']];
const TRI = [['desconhecido', 'Desconhecido'], ['sim', 'Sim'], ['nao', 'Não']];
const MAX_FOTOS = 12;

const Field = ({ label, children, hint }) => (
  <label className="flex flex-col gap-1 text-sm">
    <span className="font-semibold text-slate-700">{label}</span>
    {children}
    {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
  </label>
);

const Section = ({ n, title, children, testId, active = true }) => (
  <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId} hidden={!active}>
    <h2 className="mb-4 inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">{n}</span>
      {title}
    </h2>
    <div className="grid gap-3 md:grid-cols-2">{children}</div>
  </section>
);

const WIZARD_STEPS = [
  'Contacto e verificação', 'Nome público', 'Tipo de anúncio', 'Características',
  'Preço e condições', 'Estado da viatura', 'Histórico TVDE', 'Descrição e fotografias',
  'Revisão e submissão',
];
const SELL_DRAFT_KEY = 'mp_sell_draft_v1';

const inputCls = 'rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-emerald-400 focus:outline-none';

export default function MarketplaceSellPage() {
  const navigate = useNavigate();
  useSeo({
    title: 'Vender a minha viatura TVDE ou Ex-TVDE | Marketplace TVDE',
    description: 'Publique a sua viatura Ex-TVDE ou oportunidade para TVDE. Dados reais verificados, nome público à sua escolha e sem pedir documentos da viatura.',
    path: '/vender-viatura',
  });

  const [f, setF] = useState(() => {
    const base = {
    primeiro_nome: '', ultimo_nome: '', email: '', telefone: '', code: '', phone_code: '',
    verification_token: '', phone_verification_token: '',
    mostrar_telefone: false, mostrar_email: false,
    nome_publico_modo: 'primeiro_nome', nome_publico: '',
    categoria: 'ex_tvde', tipo_vendedor: 'particular',
    distrito: '', concelho: '', localidade: '',
    marca: '', modelo: '', versao: '', equipamento: '', ano: '', mes_matricula: '',
    quilometros: '', origem: 'nacional', pais_origem: '', ano_importacao: '',
    combustivel: 'gasoleo', caixa: 'manual', potencia: '', cilindrada: '', tracao: '',
    lugares: '', portas: '', cor: '',
    preco: '', preco_negociavel: false, financiamento: false, retoma: false,
    garantia: false, garantia_meses: '', venda_urgente: false,
    estado_principal: 'operacional', estado_geral: '', estado_mecanico: '', estado_pintura: '',
    estado_interior: '', estado_pneus: '', avarias: '', acidentes: '', manutencao: '',
    livro_revisoes: 'desconhecido', chaves: '', inspecao_valida: 'desconhecido', proxima_inspecao: '',
    bateria_estado: 'desconhecido', bateria_percentagem: '', autonomia: '',
    cabo_carregamento: false, tipo_carregamento: '',
    tvde_trabalhou: true, tvde_ano_inicio: '', tvde_ano_fim: '', tvde_meses: '',
    tvde_plataformas: '', tvde_categorias: '', tvde_motivo: '', porque_oportunidade: '',
    titulo: '', descricao: '', equipamentos: '', defeitos: '',
    };
    try {
      const d = JSON.parse(localStorage.getItem(SELL_DRAFT_KEY) || 'null');
      // Tokens de verificação nunca são restaurados do rascunho
      return d ? { ...base, ...d, verification_token: '', phone_verification_token: '', code: '', phone_code: '' } : base;
    } catch { return base; }
  });
  const [step, setStep] = useState(0);
  const [fotos, setFotos] = useState([]);
  const [principal, setPrincipal] = useState(0);
  const [emailState, setEmailState] = useState({ sending: false, sent: false, verified: false, err: '' });
  const [phoneState, setPhoneState] = useState({ sending: false, sent: false, verified: false, err: '' });
  const [state, setState] = useState({ loading: false, err: '', ok: '' });
  const [uploading, setUploading] = useState(false);

  const set = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setF((p) => ({ ...p, [k]: value }));
  };

  // Guardar rascunho (sem tokens/códigos de verificação)
  useEffect(() => {
    const { verification_token, phone_verification_token, code, phone_code, ...safe } = f;
    try { localStorage.setItem(SELL_DRAFT_KEY, JSON.stringify(safe)); } catch (_) {}
  }, [f]);

  const canNext = (s) => {
    switch (s) {
      case 0: return f.primeiro_nome.trim() && f.ultimo_nome.trim() && f.verification_token && f.phone_verification_token;
      case 2: return !!f.distrito.trim() && !!f.concelho.trim();
      case 3: return f.marca.trim() && f.modelo.trim() && f.ano && f.quilometros;
      case 4: return Number(f.preco) > 0;
      case 7: return !!f.descricao.trim();
      default: return true;
    }
  };
  const goNext = () => {
    if (!canNext(step)) { setState((p) => ({ ...p, err: 'Preencha os campos obrigatórios deste passo.' })); return; }
    setState((p) => ({ ...p, err: '' }));
    setStep((s) => Math.min(WIZARD_STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const goPrev = () => { setState((p) => ({ ...p, err: '' })); setStep((s) => Math.max(0, s - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const eletrico = f.combustivel === 'eletrico' || f.combustivel === 'hibrido_plugin';
  const tituloAuto = useMemo(() => {
    const parts = [f.marca, f.modelo, f.versao, f.ano, f.quilometros ? `${Number(f.quilometros).toLocaleString('pt-PT')} km` : ''];
    return parts.filter(Boolean).join(' · ');
  }, [f.marca, f.modelo, f.versao, f.ano, f.quilometros]);

  const sendEmailCode = async () => {
    setEmailState({ sending: true, sent: false, verified: false, err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-codigo', { email: f.email });
      setEmailState({ sending: false, sent: true, verified: false, err: '' });
    } catch (e) {
      setEmailState({ sending: false, sent: false, verified: false, err: e.response?.data?.detail || 'Falha ao enviar código.' });
    }
  };
  const verifyEmail = async () => {
    try {
      const r = await api.post('/marketplace/verificacao/confirmar', { email: f.email, code: f.code });
      setF((p) => ({ ...p, verification_token: r.data.verification_token }));
      setEmailState((s) => ({ ...s, verified: true, err: '' }));
    } catch (e) {
      setEmailState((s) => ({ ...s, verified: false, err: e.response?.data?.detail || 'Código inválido.' }));
    }
  };
  const sendSms = async () => {
    setPhoneState({ sending: true, sent: false, verified: false, err: '' });
    try {
      await api.post('/marketplace/verificacao/enviar-sms', { telefone: f.telefone });
      setPhoneState({ sending: false, sent: true, verified: false, err: '' });
    } catch (e) {
      setPhoneState({ sending: false, sent: false, verified: false, err: e.response?.data?.detail || 'Falha ao enviar SMS.' });
    }
  };
  const verifySms = async () => {
    try {
      const r = await api.post('/marketplace/verificacao/confirmar-sms', { telefone: f.telefone, code: f.phone_code });
      setF((p) => ({ ...p, phone_verification_token: r.data.verification_token }));
      setPhoneState((s) => ({ ...s, verified: true, err: '' }));
    } catch (e) {
      setPhoneState((s) => ({ ...s, verified: false, err: e.response?.data?.detail || 'Código SMS inválido.' }));
    }
  };

  const uploadPhotos = async (files) => {
    const list = Array.from(files).slice(0, MAX_FOTOS - fotos.length);
    if (!list.length) return;
    setUploading(true);
    const next = [...fotos];
    for (const file of list) {
      const data = new FormData();
      data.append('file', file);
      try {
        const r = await api.post('/marketplace/uploads', data, { headers: { 'Content-Type': 'multipart/form-data' } });
        next.push(r.data.url);
      } catch (e) {
        setState((s) => ({ ...s, err: e.response?.data?.detail || 'Falha no upload de uma imagem.' }));
      }
    }
    setFotos(next.slice(0, MAX_FOTOS));
    setUploading(false);
  };

  const movePhoto = (i, dir) => {
    const next = [...fotos];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setFotos(next);
    if (principal === i) setPrincipal(j);
    else if (principal === j) setPrincipal(i);
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setState({ loading: true, err: '', ok: '' });
    try {
      const payload = {
        primeiro_nome: f.primeiro_nome, ultimo_nome: f.ultimo_nome, email: f.email, telefone: f.telefone,
        verification_token: f.verification_token, phone_verification_token: f.phone_verification_token,
        mostrar_telefone: f.mostrar_telefone, mostrar_email: f.mostrar_email,
        nome_publico_modo: f.nome_publico_modo, nome_publico: f.nome_publico || null,
        categoria: f.categoria, tipo_vendedor: f.tipo_vendedor,
        distrito: f.distrito, concelho: f.concelho, localidade: f.localidade || null,
        viatura: {
          marca: f.marca, modelo: f.modelo, versao: f.versao || null, equipamento: f.equipamento || null,
          ano: Number(f.ano), mes_matricula: f.mes_matricula ? Number(f.mes_matricula) : null,
          quilometros: Number(f.quilometros), origem: f.origem,
          pais_origem: f.origem === 'importado' ? f.pais_origem : null,
          ano_importacao: f.ano_importacao ? Number(f.ano_importacao) : null,
          combustivel: f.combustivel, caixa: f.caixa,
          potencia: f.potencia ? Number(f.potencia) : null,
          cilindrada: f.cilindrada ? Number(f.cilindrada) : null,
          tracao: f.tracao || null, lugares: f.lugares ? Number(f.lugares) : null,
          portas: f.portas ? Number(f.portas) : null, cor: f.cor || null,
        },
        comercial: {
          preco: Number(f.preco), preco_negociavel: f.preco_negociavel, financiamento: f.financiamento,
          retoma: f.retoma, garantia: f.garantia,
          garantia_meses: f.garantia ? Number(f.garantia_meses || 0) : null,
          venda_urgente: f.venda_urgente,
        },
        estado: {
          principal: f.estado_principal, geral: f.estado_geral || null, mecanico: f.estado_mecanico || null,
          pintura: f.estado_pintura || null, interior: f.estado_interior || null, pneus: f.estado_pneus || null,
          avarias: f.avarias || null, acidentes: f.acidentes || null, manutencao: f.manutencao || null,
          livro_revisoes: f.livro_revisoes, chaves: f.chaves ? Number(f.chaves) : null,
          inspecao_valida: f.inspecao_valida, proxima_inspecao: f.proxima_inspecao || null,
          bateria_estado: eletrico ? f.bateria_estado : null,
          bateria_percentagem: eletrico && f.bateria_percentagem ? Number(f.bateria_percentagem) : null,
          autonomia: eletrico && f.autonomia ? Number(f.autonomia) : null,
          cabo_carregamento: eletrico ? f.cabo_carregamento : null,
          tipo_carregamento: eletrico ? (f.tipo_carregamento || null) : null,
        },
        historico_tvde: f.categoria === 'ex_tvde' ? {
          trabalhou: true,
          ano_inicio: f.tvde_ano_inicio ? Number(f.tvde_ano_inicio) : null,
          ano_fim: f.tvde_ano_fim ? Number(f.tvde_ano_fim) : null,
          meses_atividade: f.tvde_meses ? Number(f.tvde_meses) : null,
          plataformas: f.tvde_plataformas ? f.tvde_plataformas.split(',').map((s) => s.trim()).filter(Boolean) : null,
          categorias: f.tvde_categorias ? f.tvde_categorias.split(',').map((s) => s.trim()).filter(Boolean) : null,
          motivo_saida: f.tvde_motivo || null,
        } : { trabalhou: false, porque_oportunidade: f.porque_oportunidade || null },
        titulo: f.titulo || null, descricao: f.descricao,
        equipamentos: f.equipamentos || null, defeitos: f.defeitos || null,
        fotos, foto_principal: principal,
      };
      const r = await api.post('/marketplace/venda/publicacoes', payload);
      try { localStorage.removeItem(SELL_DRAFT_KEY); } catch (_) {}
      try { const { track } = await import('@/lib/analytics'); track('buy_promotion', { finalidade: 'venda' }); } catch (_) {}
      setState({ loading: false, err: '', ok: r.data.message || 'Anúncio recebido.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      const detail = e.response?.data?.detail;
      let msg = 'Não foi possível publicar o anúncio. Reveja os campos.';
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        const campos = detail
          .map((d) => (d.loc || []).filter((l) => l !== 'body').join(' → '))
          .filter(Boolean)
          .slice(0, 4)
          .join(', ');
        msg = `Reveja os campos obrigatórios: ${campos || 'dados incompletos'}.`;
      }
      setState({ loading: false, err: msg, ok: '' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (state.ok) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-2xl px-4 py-20 text-center" data-testid="sell-success">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
            <Check className="h-7 w-7 text-emerald-600" />
          </div>
          <h1 className="mt-5 text-3xl font-extrabold text-slate-900">Anúncio de venda recebido</h1>
          <p className="mt-3 text-slate-600">{state.ok} Vai ser validado pela nossa equipa antes de ficar público.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Link to="/gerir-anuncio" className="rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-bold text-white" data-testid="sell-manage-link">Gerir o meu anúncio</Link>
            <Link to="/comprar-viaturas-tvde" className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-800">Ver viaturas à venda</Link>
          </div>
        </div>
        <MarketplaceFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="sell-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-[1000px] items-center justify-between px-4 py-4 md:px-8">
          <button onClick={() => navigate('/comprar-viaturas-tvde')} className="inline-flex items-center gap-2 text-sm font-bold text-slate-900" data-testid="sell-back">
            <ArrowLeft className="h-4 w-4" /> Comprar viaturas
          </button>
          <Link to="/" className="text-xs font-semibold text-slate-500">Marketplace TVDE</Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-[1000px] px-4 py-8 md:px-8">
        <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl" data-testid="sell-title">
          Vender uma viatura
        </h1>
        <p className="mt-3 max-w-2xl text-base text-slate-600">
          Publique uma viatura <strong>Ex-TVDE</strong> ou uma <strong>oportunidade para TVDE</strong>. Não pedimos
          documentos, matrícula, VIN nem morada completa.
        </p>

        {state.err && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="sell-error">
            {state.err}
          </div>
        )}

        <form onSubmit={submit} noValidate className="mt-6 space-y-4">
          {/* Indicador de progresso (Bloco 9) */}
          <div data-testid="sell-wizard-progress">
            <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
              <span>Passo {step + 1} de {WIZARD_STEPS.length}</span>
              <span className="text-emerald-600">{WIZARD_STEPS[step]}</span>
            </div>
            <div className="mt-1 h-1.5 w-full rounded-full bg-slate-200">
              <div className="h-1.5 rounded-full bg-emerald-500 transition-all" style={{ width: `${((step + 1) / WIZARD_STEPS.length) * 100}%` }} />
            </div>
          </div>
          <Section n="1" title="Identificação privada do anunciante" testId="sell-section-identity" active={step === 0}>
            <Field label="Primeiro nome"><input className={inputCls} value={f.primeiro_nome} onChange={set('primeiro_nome')} data-testid="sell-first-name" required /></Field>
            <Field label="Último nome"><input className={inputCls} value={f.ultimo_nome} onChange={set('ultimo_nome')} data-testid="sell-last-name" required /></Field>
            <Field label="Email"><input type="email" className={inputCls} value={f.email} onChange={set('email')} data-testid="sell-email" required /></Field>
            <Field label="Telemóvel"><input className={inputCls} value={f.telefone} onChange={set('telefone')} data-testid="sell-phone" required /></Field>

            <div className="md:col-span-2 space-y-2 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/50 p-3">
              <p className="text-xs font-semibold text-slate-600">Verificação do email (obrigatória)</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={sendEmailCode} disabled={emailState.sending || emailState.verified} className="rounded-xl border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700 disabled:opacity-60" data-testid="sell-send-code">
                  {emailState.verified ? '✓ Verificado' : emailState.sending ? 'A enviar…' : emailState.sent ? '✓ Reenviar' : 'Enviar código'}
                </button>
                {!emailState.verified && <input className={`${inputCls} flex-1`} placeholder="Código de 6 dígitos" value={f.code} onChange={set('code')} data-testid="sell-code" />}
                {!emailState.verified && <button type="button" onClick={verifyEmail} className="rounded-xl border border-emerald-300 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="sell-verify-code">Validar</button>}
              </div>
              {emailState.err && <p className="text-xs text-rose-600" data-testid="sell-code-error">{emailState.err}</p>}
            </div>

            <div className="md:col-span-2 space-y-2 rounded-xl border border-dashed border-cyan-300 bg-cyan-50/50 p-3">
              <p className="text-xs font-semibold text-slate-600">Verificação do telemóvel (obrigatória)</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={sendSms} disabled={phoneState.sending || phoneState.verified} className="rounded-xl border border-cyan-300 px-3 py-2 text-xs font-semibold text-cyan-700 disabled:opacity-60" data-testid="sell-send-sms">
                  {phoneState.verified ? '✓ Verificado' : phoneState.sending ? 'A enviar…' : phoneState.sent ? '✓ Reenviar SMS' : 'Enviar SMS'}
                </button>
                {!phoneState.verified && <input className={`${inputCls} flex-1`} placeholder="Código do SMS" value={f.phone_code} onChange={set('phone_code')} data-testid="sell-sms-code" />}
                {!phoneState.verified && <button type="button" onClick={verifySms} className="rounded-xl border border-cyan-300 px-3 py-2 text-xs font-semibold text-cyan-700" data-testid="sell-verify-sms">Validar SMS</button>}
              </div>
              {phoneState.err && <p className="text-xs text-rose-600" data-testid="sell-sms-error">{phoneState.err}</p>}
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={f.mostrar_telefone} onChange={set('mostrar_telefone')} data-testid="sell-show-phone" /> Mostrar o meu telemóvel no anúncio
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={f.mostrar_email} onChange={set('mostrar_email')} data-testid="sell-show-email" /> Mostrar o meu email no anúncio
            </label>
          </Section>

          <Section n="2" title="Nome apresentado no anúncio" testId="sell-section-publicname" active={step === 1}>
            <Field label="Como quer aparecer">
              <select className={inputCls} value={f.nome_publico_modo} onChange={set('nome_publico_modo')} data-testid="sell-public-name-mode">
                <option value="primeiro_nome">Apenas o primeiro nome</option>
                <option value="primeiro_inicial">Primeiro nome e inicial do apelido</option>
                <option value="alcunha">Nome público ou alcunha escolhida</option>
              </select>
            </Field>
            {f.nome_publico_modo === 'alcunha' && (
              <Field label="Nome público" hint="Sem telefone, email, links ou nomes de empresas/entidades.">
                <input className={inputCls} value={f.nome_publico} onChange={set('nome_publico')} data-testid="sell-public-name" />
              </Field>
            )}
            <p className="md:col-span-2 inline-flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-600">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              O nome público é apresentado no anúncio. Os seus dados reais verificados permanecem privados e são
              utilizados para segurança, contacto e moderação.
            </p>
          </Section>

          <Section n="3" title="Tipo de anúncio e vendedor" testId="sell-section-type" active={step === 2}>
            <Field label="Categoria">
              <select className={inputCls} value={f.categoria} onChange={set('categoria')} data-testid="sell-categoria">
                <option value="ex_tvde">Ex-TVDE (já trabalhou em TVDE)</option>
                <option value="oportunidade">Oportunidade para TVDE (nunca trabalhou)</option>
              </select>
            </Field>
            <Field label="Tipo de vendedor">
              <select className={inputCls} value={f.tipo_vendedor} onChange={set('tipo_vendedor')} data-testid="sell-vendedor">
                {VENDEDORES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Distrito"><input className={inputCls} value={f.distrito} onChange={set('distrito')} data-testid="sell-distrito" required /></Field>
            <Field label="Concelho"><input className={inputCls} value={f.concelho} onChange={set('concelho')} data-testid="sell-concelho" required /></Field>
            <Field label="Cidade ou localidade aproximada" hint="Não pedimos morada completa nem localização exata.">
              <input className={inputCls} value={f.localidade} onChange={set('localidade')} data-testid="sell-localidade" />
            </Field>
          </Section>

          <Section n="4" title="Identificação da viatura" testId="sell-section-vehicle" active={step === 3}>
            <Field label="Marca"><input className={inputCls} value={f.marca} onChange={set('marca')} data-testid="sell-marca" required /></Field>
            <Field label="Modelo"><input className={inputCls} value={f.modelo} onChange={set('modelo')} data-testid="sell-modelo" required /></Field>
            <Field label="Versão"><input className={inputCls} value={f.versao} onChange={set('versao')} data-testid="sell-versao" /></Field>
            <Field label="Nível de equipamento (opcional)"><input className={inputCls} value={f.equipamento} onChange={set('equipamento')} data-testid="sell-equipamento" /></Field>
            <Field label="Ano"><input type="number" min="1980" max={new Date().getFullYear()} className={inputCls} value={f.ano} onChange={set('ano')} data-testid="sell-ano" required /></Field>
            <Field label="Mês da primeira matrícula"><input type="number" min="1" max="12" className={inputCls} value={f.mes_matricula} onChange={set('mes_matricula')} data-testid="sell-mes" /></Field>
            <Field label="Quilómetros"><input type="number" min="0" className={inputCls} value={f.quilometros} onChange={set('quilometros')} data-testid="sell-km" required /></Field>
            <Field label="Nacional ou importado">
              <select className={inputCls} value={f.origem} onChange={set('origem')} data-testid="sell-origem">
                <option value="nacional">Nacional</option><option value="importado">Importado</option>
              </select>
            </Field>
            {f.origem === 'importado' && (
              <>
                <Field label="País de origem"><input className={inputCls} value={f.pais_origem} onChange={set('pais_origem')} data-testid="sell-pais-origem" required /></Field>
                <Field label="Ano de importação (opcional)"><input type="number" className={inputCls} value={f.ano_importacao} onChange={set('ano_importacao')} data-testid="sell-ano-importacao" /></Field>
              </>
            )}
            <Field label="Combustível">
              <select className={inputCls} value={f.combustivel} onChange={set('combustivel')} data-testid="sell-combustivel">
                {FUEL.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Caixa">
              <select className={inputCls} value={f.caixa} onChange={set('caixa')} data-testid="sell-caixa">
                <option value="manual">Manual</option><option value="automatica">Automática</option>
              </select>
            </Field>
            <Field label="Potência (cv)"><input type="number" min="0" className={inputCls} value={f.potencia} onChange={set('potencia')} data-testid="sell-potencia" /></Field>
            <Field label="Cilindrada (cm³)"><input type="number" min="0" className={inputCls} value={f.cilindrada} onChange={set('cilindrada')} data-testid="sell-cilindrada" /></Field>
            <Field label="Tração (opcional)"><input className={inputCls} value={f.tracao} onChange={set('tracao')} data-testid="sell-tracao" /></Field>
            <Field label="Número de lugares"><input type="number" min="1" max="9" className={inputCls} value={f.lugares} onChange={set('lugares')} data-testid="sell-lugares" /></Field>
            <Field label="Número de portas (opcional)"><input type="number" min="2" max="6" className={inputCls} value={f.portas} onChange={set('portas')} data-testid="sell-portas" /></Field>
            <Field label="Cor"><input className={inputCls} value={f.cor} onChange={set('cor')} data-testid="sell-cor" /></Field>
          </Section>

          <Section n="5" title="Preço e condições comerciais" testId="sell-section-price" active={step === 4}>
            <Field label="Preço de venda (€)"><input type="number" min="1" className={inputCls} value={f.preco} onChange={set('preco')} data-testid="sell-preco" required /></Field>
            <div className="grid gap-2">
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.preco_negociavel} onChange={set('preco_negociavel')} data-testid="sell-negociavel" /> Preço negociável</label>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.financiamento} onChange={set('financiamento')} data-testid="sell-financiamento" /> Financiamento disponível (declarado)</label>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.retoma} onChange={set('retoma')} data-testid="sell-retoma" /> Aceito retoma (declarado)</label>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.garantia} onChange={set('garantia')} data-testid="sell-garantia" /> Garantia declarada</label>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.venda_urgente} onChange={set('venda_urgente')} data-testid="sell-urgente" /> Venda urgente</label>
            </div>
            {f.garantia && <Field label="Duração da garantia (meses)"><input type="number" min="1" className={inputCls} value={f.garantia_meses} onChange={set('garantia_meses')} data-testid="sell-garantia-meses" /></Field>}
            <p className="md:col-span-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Financiamento, garantia e retoma são declarações do anunciante e não serviços garantidos pelo Marketplace.
            </p>
          </Section>

          <Section n="6" title="Estado da viatura" testId="sell-section-condition" active={step === 5}>
            <Field label="Estado principal (obrigatório)">
              <select className={inputCls} value={f.estado_principal} onChange={set('estado_principal')} data-testid="sell-estado-principal">
                {ESTADOS.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Estado geral"><input className={inputCls} value={f.estado_geral} onChange={set('estado_geral')} data-testid="sell-estado-geral" /></Field>
            <Field label="Estado mecânico declarado"><input className={inputCls} value={f.estado_mecanico} onChange={set('estado_mecanico')} /></Field>
            <Field label="Pintura e chapa"><input className={inputCls} value={f.estado_pintura} onChange={set('estado_pintura')} /></Field>
            <Field label="Interior"><input className={inputCls} value={f.estado_interior} onChange={set('estado_interior')} /></Field>
            <Field label="Pneus"><input className={inputCls} value={f.estado_pneus} onChange={set('estado_pneus')} /></Field>
            <Field label="Avarias ou anomalias conhecidas"><textarea rows={2} className={inputCls} value={f.avarias} onChange={set('avarias')} data-testid="sell-avarias" /></Field>
            <Field label="Acidentes ou danos conhecidos"><textarea rows={2} className={inputCls} value={f.acidentes} onChange={set('acidentes')} /></Field>
            <Field label="Histórico de manutenção declarado"><textarea rows={2} className={inputCls} value={f.manutencao} onChange={set('manutencao')} /></Field>
            <Field label="Livro de revisões">
              <select className={inputCls} value={f.livro_revisoes} onChange={set('livro_revisoes')} data-testid="sell-livro">
                {TRI.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Número de chaves"><input type="number" min="0" max="6" className={inputCls} value={f.chaves} onChange={set('chaves')} /></Field>
            <Field label="Inspeção válida">
              <select className={inputCls} value={f.inspecao_valida} onChange={set('inspecao_valida')} data-testid="sell-inspecao">
                {TRI.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Data aproximada da próxima inspeção (opcional)"><input className={inputCls} placeholder="MM/AAAA" value={f.proxima_inspecao} onChange={set('proxima_inspecao')} /></Field>
            {eletrico && (
              <>
                <Field label="Estado da bateria">
                  <select className={inputCls} value={f.bateria_estado} onChange={set('bateria_estado')} data-testid="sell-bateria-estado">
                    <option value="desconhecido">Desconhecido</option><option value="declarado">Percentagem declarada</option>
                  </select>
                </Field>
                {f.bateria_estado === 'declarado' && (
                  <Field label="Percentagem declarada (%)"><input type="number" min="0" max="100" className={inputCls} value={f.bateria_percentagem} onChange={set('bateria_percentagem')} data-testid="sell-bateria-pct" /></Field>
                )}
                <Field label="Autonomia aproximada declarada (km)"><input type="number" min="0" className={inputCls} value={f.autonomia} onChange={set('autonomia')} data-testid="sell-autonomia" /></Field>
                <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.cabo_carregamento} onChange={set('cabo_carregamento')} data-testid="sell-cabo" /> Cabo de carregamento incluído</label>
                <Field label="Tipo de carregamento (opcional)"><input className={inputCls} value={f.tipo_carregamento} onChange={set('tipo_carregamento')} /></Field>
              </>
            )}
            <p className="md:col-span-2 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-500">
              Toda esta informação é declarada pelo anunciante e não é verificada pelo Marketplace.
            </p>
          </Section>

          <Section n="7" title="Histórico TVDE" testId="sell-section-tvde" active={step === 6}>
            {f.categoria === 'ex_tvde' ? (
              <>
                <p className="md:col-span-2 rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-800" data-testid="sell-tvde-confirm">
                  Confirma que esta viatura já trabalhou em TVDE e já não está em atividade.
                </p>
                <Field label="Ano em que começou"><input type="number" min="2010" max={new Date().getFullYear()} className={inputCls} value={f.tvde_ano_inicio} onChange={set('tvde_ano_inicio')} data-testid="sell-tvde-inicio" /></Field>
                <Field label="Ano em que deixou a atividade"><input type="number" min="2010" max={new Date().getFullYear()} className={inputCls} value={f.tvde_ano_fim} onChange={set('tvde_ano_fim')} data-testid="sell-tvde-fim" /></Field>
                <Field label="Meses aproximados em atividade"><input type="number" min="0" className={inputCls} value={f.tvde_meses} onChange={set('tvde_meses')} /></Field>
                <Field label="Plataformas utilizadas (opcional)" hint="Declaração do anunciante."><input className={inputCls} value={f.tvde_plataformas} onChange={set('tvde_plataformas')} placeholder="Separadas por vírgula" /></Field>
                <Field label="Categorias utilizadas (declaração)"><input className={inputCls} value={f.tvde_categorias} onChange={set('tvde_categorias')} placeholder="Separadas por vírgula" /></Field>
                <Field label="Motivo de saída (opcional)"><input className={inputCls} value={f.tvde_motivo} onChange={set('tvde_motivo')} /></Field>
              </>
            ) : (
              <>
                <p className="md:col-span-2 rounded-xl bg-sky-50 px-3 py-2 text-xs text-sky-800" data-testid="sell-oportunidade-confirm">
                  Confirma que esta viatura nunca trabalhou em TVDE.
                </p>
                <Field label="Porque considera esta viatura uma oportunidade">
                  <textarea rows={3} className={inputCls} value={f.porque_oportunidade} onChange={set('porque_oportunidade')} data-testid="sell-porque-oportunidade" />
                </Field>
              </>
            )}
            <p className="md:col-span-2 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Qualquer compatibilidade com serviços ou categorias TVDE é declarada pelo anunciante e deve ser
              confirmada pelo interessado junto das plataformas e entidades aplicáveis.
            </p>
          </Section>

          <Section n="8" title="Descrição e fotografias" testId="sell-section-content" active={step === 7}>
            <Field label="Título do anúncio" hint={tituloAuto ? `Sugestão automática: ${tituloAuto}` : 'Preenche automaticamente com marca, modelo, ano e km.'}>
              <input className={inputCls} value={f.titulo} onChange={set('titulo')} placeholder={tituloAuto} data-testid="sell-titulo" />
            </Field>
            <Field label="Equipamentos e extras"><textarea rows={2} className={inputCls} value={f.equipamentos} onChange={set('equipamentos')} data-testid="sell-equipamentos" /></Field>
            <Field label="Descrição detalhada"><textarea rows={5} className={inputCls} value={f.descricao} onChange={set('descricao')} data-testid="sell-descricao" required /></Field>
            <Field label="Defeitos ou aspetos a conhecer"><textarea rows={5} className={inputCls} value={f.defeitos} onChange={set('defeitos')} data-testid="sell-defeitos" /></Field>

            <div className="md:col-span-2">
              <p className="text-sm font-semibold text-slate-700">Fotografias (até {MAX_FOTOS})</p>
              <label className="mt-2 inline-flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-emerald-300 bg-emerald-50/40 px-4 py-3 text-sm font-semibold text-emerald-700">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                {uploading ? 'A carregar…' : 'Escolher fotografias'}
                <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} data-testid="sell-photo-input" />
              </label>
              <p className="mt-1 text-[11px] text-slate-400">JPG, PNG ou WebP. Removemos automaticamente os metadados (EXIF) e corrigimos a rotação.</p>
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6" data-testid="sell-photo-list">
                {fotos.map((url, i) => (
                  <div key={url} className={`relative overflow-hidden rounded-xl border-2 ${principal === i ? 'border-emerald-500' : 'border-slate-200'}`} data-testid={`sell-photo-${i}`}>
                    <img src={resolveImageUrl(url)} alt={`Foto ${i + 1}`} className="aspect-square w-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between bg-black/50 px-1 py-0.5">
                      <button type="button" onClick={() => movePhoto(i, -1)} className="text-[10px] font-bold text-white" data-testid={`sell-photo-left-${i}`}>◀</button>
                      <button type="button" onClick={() => setPrincipal(i)} title="Definir como principal" data-testid={`sell-photo-main-${i}`}>
                        <Star className={`h-3.5 w-3.5 ${principal === i ? 'text-amber-300' : 'text-white'}`} />
                      </button>
                      <button type="button" onClick={() => { setFotos(fotos.filter((_, j) => j !== i)); setPrincipal(0); }} data-testid={`sell-photo-remove-${i}`}>
                        <Trash2 className="h-3.5 w-3.5 text-white" />
                      </button>
                      <button type="button" onClick={() => movePhoto(i, 1)} className="text-[10px] font-bold text-white" data-testid={`sell-photo-right-${i}`}>▶</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Section>

          {/* Passo 9 — Revisão e submissão */}
          <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="sell-section-review" hidden={step !== 8}>
            <h2 className="mb-4 inline-flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-[11px] font-bold text-white">9</span>
              Revisão e submissão
            </h2>
            <ul className="mb-4 grid gap-1 rounded-xl bg-slate-50 p-3 text-[12px] text-slate-600 md:grid-cols-2" data-testid="sell-review-summary">
              <li><strong>{f.titulo || tituloAuto || '(sem título)'}</strong></li>
              <li>{f.marca} {f.modelo} {f.ano} · {f.quilometros ? `${f.quilometros} km` : '—'}</li>
              <li>{f.distrito} · {f.concelho}</li>
              <li>Preço: <strong>{f.preco || '—'} €</strong>{f.preco_negociavel ? ' (negociável)' : ''}</li>
              <li>{fotos.length} foto(s){fotos.length ? ` · principal #${principal + 1}` : ''}</li>
              <li>{f.mostrar_email ? 'Email visível' : 'Email oculto'} · {f.mostrar_telefone ? 'Tel. visível' : 'Tel. oculto'}</li>
            </ul>
            <p className="text-xs text-slate-500">
              Não pedimos nem guardamos DUA, VIN, número de chassis, matrícula completa, morada completa, apólices,
              documentos da viatura ou documentos pessoais. Não existem selos de "viatura verificada".
            </p>
            <button
              type="submit"
              disabled={state.loading || !f.verification_token || !f.phone_verification_token}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-bold text-white transition hover:bg-emerald-600 disabled:opacity-50 md:w-auto"
              data-testid="sell-submit"
            >
              {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Publicar anúncio de venda
            </button>
            {(!f.verification_token || !f.phone_verification_token) && (
              <p className="mt-2 text-xs text-slate-500" data-testid="sell-verify-hint">
                Verifique o email e o telemóvel para poder publicar.
              </p>
            )}
          </section>

          {/* Navegação do wizard */}
          <div className="flex items-center justify-between" data-testid="sell-wizard-nav">
            <button type="button" onClick={goPrev} disabled={step === 0}
              className="inline-flex items-center gap-1 rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 disabled:opacity-40" data-testid="sell-wizard-prev">
              <ArrowLeft className="h-4 w-4" /> Voltar
            </button>
            {step < WIZARD_STEPS.length - 1 && (
              <button type="button" onClick={goNext}
                className="inline-flex items-center gap-1 rounded-xl bg-slate-900 px-5 py-2 text-sm font-bold text-white hover:bg-slate-800" data-testid="sell-wizard-next">
                Continuar
              </button>
            )}
          </div>
        </form>
      </main>
      <MarketplaceFooter />
    </div>
  );
}
